import {
  ActivelyEngaging,
  CommentScoreIncreased,
  DefaultEmptyState,
  NoCommentsFound,
  RepliedToManyComments,
} from '../EmptyStates'
import { COMMENT_STATUS, type CommentStatusWithAll } from '../../types'
import { useCommentsEngagementData } from './useCommentsListData'

type UseEmptyStateRendererProps = {
  channelId: string
  hasComments: boolean
  hasActiveFilters: boolean
  status: CommentStatusWithAll | undefined
  onClearFilters: () => void
}

export const useEmptyStateRenderer = ({
  channelId,
  hasComments,
  hasActiveFilters,
  status,
  onClearFilters,
}: UseEmptyStateRendererProps): { EmptyState: JSX.Element | null } => {
  const engagementData = useCommentsEngagementData({ channelId })

  const { score, engagement } = engagementData
  const { hasIncreased, current: currentScore, delta: scoreDelta } = score
  const { isActivelyEngaging, activeTime, repliedToManyComments } = engagement

  const scoreIncreased =
    hasIncreased && currentScore != null && scoreDelta != null

  // If we have comments, no empty state needed
  if (hasComments) {
    return { EmptyState: null }
  }

  // Handle 'unreplied' status with priority-based empty states
  if (status === COMMENT_STATUS.Unreplied) {
    // Priority 1: Score increased
    if (scoreIncreased) {
      return {
        EmptyState: (
          <CommentScoreIncreased score={currentScore} scoreDelta={scoreDelta} />
        ),
      }
    }
    // Priority 2: Actively engaging
    if (isActivelyEngaging) {
      return {
        EmptyState: <ActivelyEngaging activeTime={activeTime} />,
      }
    }
    // Priority 3: Replied to at least 20 comments
    if (repliedToManyComments) {
      return {
        EmptyState: <RepliedToManyComments />,
      }
    }
    // Default for 'unreplied' if no special conditions met
    return {
      EmptyState: <DefaultEmptyState />,
    }
  }

  // Generic 'No Comments Found' for other statuses
  return {
    EmptyState: (
      <NoCommentsFound
        status={status}
        hasActiveFilters={hasActiveFilters}
        onClearFilters={onClearFilters}
      />
    ),
  }
}
